import { Component, computed, inject, input, output, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { UiCallFlow } from '../call-flows-side-nav/call-flows-side-nav.component';
import { WeeklyScheduleComponent } from '../weekly-schedule/weekly-schedule.component';
import { FormsModule } from '@angular/forms';
import { HolidayCallFlowFormComponent } from '../holidays/holiday-call-flow-form.component';
import {
  CallFlowGreetingsEntity,
  CallFlowGreetingsFormComponent,
} from '../call-flow-greetings/call-flow-greetings-form.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { AutoAttendantHoliday, WeeklySchedule } from '../../../../shared/models/generated/ziroModels';
import { TeamsAutoAttendantEditContext } from '../../../teams-auto-attendant-edit.context';
import { CallFlowDestinationsFormComponent } from '../call-flow-destinations/call-flow-destinations-form.component';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';

@Component({
  selector: 'ziro-auto-attendants-call-flow-edit',
  imports: [
    TranslateModule,
    WeeklyScheduleComponent,
    FormsModule,
    HolidayCallFlowFormComponent,
    CallFlowGreetingsFormComponent,
    ZiroFormsModule,
    CallFlowDestinationsFormComponent,
  ],
  templateUrl: './teams-auto-attendants-call-flow-edit.component.html',
})
export class TeamsAutoAttendantsCallFlowEditComponent {
  private _editContext = inject(TeamsAutoAttendantEditContext);

  @ViewChild(HolidayCallFlowFormComponent) holidayForm?: HolidayCallFlowFormComponent;
  @ViewChild(CallFlowGreetingsFormComponent) greetingsForm?: CallFlowGreetingsFormComponent;
  @ViewChild(CallFlowDestinationsFormComponent) destinationsForm?: CallFlowDestinationsFormComponent;

  // inputs
  tenantId = input.required<string>();
  callFlow = input.required<UiCallFlow>();
  allHolidays = input.required<AutoAttendantHoliday[]>();

  // outputs
  callFlowNameChange = output<string>();
  greetingUpdate = output<CallFlowGreetingsEntity>();
  navigateToBusinessHours = output<void>();
  validityChange = output<{ flowId: string; isValid: boolean }>();

  currentGreeting = computed(() => {
    const state = this._editContext.getFlowState(this.callFlow().id);
    return state?.greetingOption ?? this.callFlow().greetingOption;
  });

  currentPendingGreetingOption = computed(() => {
    const state = this._editContext.getFlowState(this.callFlow().id);
    return state?.pendingGreetingOption ?? this.callFlow().selectedGreetingOption;
  });

  currentSchedule = computed(() => {
    const state = this._editContext.getFlowState(this.callFlow().id);
    return state?.schedule ?? this.callFlow().schedule;
  });

  private _holidayValid = true;
  private _greetingsValid = true;

  onScheduleChange(schedule: WeeklySchedule): void {
    this._editContext.updateSchedule(this.callFlow().id, schedule);
  }

  onHolidayValidityChange(isValid: boolean): void {
    this._holidayValid = isValid;
    this._emitValidity();
  }

  onGreetingsValidityChange(isValid: boolean): void {
    this._greetingsValid = isValid;
    this._emitValidity();
  }

  validateAndShowErrors(): boolean {
    if (this.holidayForm) {
      this.holidayForm.triggerSubmitValidation();
    }
    if (this.greetingsForm) {
      this.greetingsForm.triggerSubmitValidation();
    }
    return this.isValid();
  }

  isValid(): boolean {
    return (
      (!this.holidayForm || this.holidayForm.isFormValid()) &&
      (!this.greetingsForm || this.greetingsForm.isFormValid()) &&
      (!this.destinationsForm || this.destinationsForm.validateAndShowErrors())
    );
  }

  private _emitValidity(): void {
    const valid = (this.callFlow().isHoliday ? this._holidayValid : true) && this._greetingsValid;
    this.validityChange.emit({ flowId: this.callFlow().id, isValid: valid });
  }

  protected readonly ZiroIcons = ZiroIcons;
}
